import { CART_ELEMENT_ID } from '../../../app/cart/constants.js';

/**
 * Delivers the resolved design tokens to the cart subtree.
 *
 * Two channels, one source of truth (see `tokens.js` for the token schema):
 *
 *   1. Emotion's `ThemeProvider` — makes the resolved token object available to
 *      every `styled` callback as `({ theme })`.
 *   2. A CSS custom property block on the cart's root element — the public
 *      override channel for embedding sites.
 *
 * ## Why the custom properties are declared inside `:where(...)`
 *
 * `:where()` has zero specificity, so this declaration block loses to *any* host
 * rule that sets the same variable — including a plain `#bouncie-embedded-cart { }`
 * or even a single class. That is the point: these are defaults, and a host should
 * be able to override them without specificity gymnastics or `!important`:
 *
 *     #bouncie-embedded-cart { --bec-color-accent: #ff6600; }
 *
 * Declaring them at the element's natural specificity (or worse, as inline styles
 * on the container) would beat the host's stylesheet and make the override channel
 * useless.
 *
 * ## Interaction with the `all: revert` host-isolation reset
 *
 * Every component built through `./styled.js` gets `all: revert` as the first
 * declaration in its rule, to stop the host page's ambient CSS from cascading in.
 * Custom properties are inherited, and `revert` rolls a property back to the value
 * it would have had with no author declaration at this level — which, for an
 * inherited property, is the parent's computed value. So the `--bec-*` values
 * declared here still reach descendants.
 *
 * This is nonetheless the one assumption in the token layer that depends on browser
 * behavior rather than our own code, so `tokens.token()` emits
 * `var(--bec-x, <default>)` rather than a bare `var(--bec-x)`. If a browser ever
 * did strip the inherited custom property, the literal fallback keeps the cart
 * rendering correctly and only the host's CSS-variable override would be lost —
 * `data-config.tokens` would still work, since that path feeds channel 1.
 */
import React, { useMemo } from 'react';
import { Global, ThemeProvider } from '@emotion/react';
import { resolveTokens, tokensToCssVars, warnUnknownTokenGroups } from './tokens.js';

const ROOT_SELECTOR = `#${CART_ELEMENT_ID}`;

const CartThemeProvider = ({ tokenOverrides, children }) => {
    const tokens = useMemo(() => {
        warnUnknownTokenGroups(tokenOverrides);
        return resolveTokens(tokenOverrides);
    }, [tokenOverrides]);

    const cssVars = useMemo(() => tokensToCssVars(tokens), [tokens]);

    return (
        <ThemeProvider theme={tokens}>
            <Global styles={{ [`:where(${ROOT_SELECTOR})`]: cssVars }} />
            {children}
        </ThemeProvider>
    );
};

export default CartThemeProvider;
